import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { fetchUserWorkSchedule } from "../../../api/user";
import { useSelector } from "react-redux";

const weekdayKeys = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export const ViewUserWorkSchedule = () => {
  const { t } = useTranslation();
  const [schedule, setSchedule] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const currentUser = useSelector((state) => state.session.currentUser);
  const userId = currentUser?.id || null;

  const weekdayLabels = useMemo(
    () => ({
      monday: t("work_schedule_view.day.monday"),
      tuesday: t("work_schedule_view.day.tuesday"),
      wednesday: t("work_schedule_view.day.wednesday"),
      thursday: t("work_schedule_view.day.thursday"),
      friday: t("work_schedule_view.day.friday"),
      saturday: t("work_schedule_view.day.saturday"),
      sunday: t("work_schedule_view.day.sunday"),
    }),
    [t]
  );

  useEffect(() => {
    let cancelled = false;

    async function loadSchedule() {
      if (!userId) {
        setIsLoading(false);
        return;
      }
      const response = await fetchUserWorkSchedule(userId);
      if (cancelled) return;
      const scaled = response?.scaled_weekdays ?? response?.weekdays;
      if (scaled && typeof scaled === "object") {
        setSchedule({ ...response, scaled_weekdays: scaled });
      } else {
        setSchedule(null);
      }
      setIsLoading(false);
    }

    loadSchedule();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  function Content() {
    if (!schedule?.scaled_weekdays || typeof schedule.scaled_weekdays !== "object") {
      return (
        <div className="mx-auto mt-8 max-w-3xl px-4 sm:px-6">
          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-sm text-slate-700">{t("work_schedule_view.empty")}</p>
            <p className="mt-3 text-xs text-slate-500">{t("work_schedule_view.empty_hint")}</p>
            <Link
              to="/dashboard/users"
              className="mt-4 inline-block text-sm font-semibold text-slate-900 underline"
            >
              {t("work_schedule_view.link_users")}
            </Link>
          </div>
        </div>
      );
    }

    const rows = weekdayKeys.map((day) => [day, schedule.scaled_weekdays[day]]);

    return (
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
          <div className="overflow-hidden rounded-lg bg-white shadow ring-1 ring-black/5">
            <div className="border-b border-gray-200 p-4">
              <h2 className="text-xl font-semibold text-gray-800">
                {schedule?.name || t("work_schedule_view.fallback_title")}
              </h2>
              <p className="text-sm text-gray-500">{t("work_schedule_view.subtitle")}</p>
            </div>
            <div className="p-4">
              <table className="w-full table-fixed border-separate border-spacing-y-2">
                <tbody>
                  {rows.map(([day, hours]) => (
                    <tr key={day} className="rounded-md bg-gray-50">
                      <td className="px-4 py-2 text-left font-medium text-gray-700">
                        {weekdayLabels[day] || day}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-900">
                        {Number(hours) > 0 ? t("work_schedule_view.hours_value", { hours }) : t("work_schedule_view.day_off")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <DashboardHeader title={t("work_schedule_view.page_title")}>
        {isLoading ? <ResourceLoading /> : <Content />}
      </DashboardHeader>
    </>
  );
};
